import { Component, inject, Input, OnInit } from '@angular/core';
import { KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { formatCost, formatCostDifference, formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-total-insurance-cost',
  standalone: true,
  imports: [KpiWidgetComponent],
  templateUrl: './total-insurance-cost.component.html',
  styleUrl: './total-insurance-cost.component.scss'
})
export class TotalInsuranceCostComponent extends BaseWidgetComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  @Input() selectedFilter: string;

  private authService = inject(AuthService);
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);

  currencySymbol: string = '';
  currencyCode: string = '';
  hasError: boolean = false;
  errorMessage: string = '';
  costDifference: number = 0;

  constructor() {
    const filterService: FilterService = inject(FilterService);
    super(filterService);
    this.title = inject(TranslocoService).translate('costTracking.widgets.totalInsuranceCost');
    this.isLoading = true;
    this.value = 0;
    this.percentageDifference = 0;

    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
    }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    void this.loadData();
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadData();
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;
    try {
      this.cancelSubject.next();
      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };
      const response = await this.costTrackingService.getTotalInsuranceCost(
        request,
        this.cancelSubject
      );
      const data = response.data;
      this.value = data?.totalValue ?? 0;
      this.totalCost = data?.previousValue ?? 0;
      this.percentageDifference = data?.percentageChange ?? 0;
      this.costDifference = data?.costDifference ?? 0;
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number): string {
    return formatValue(value, { currencySymbol: this.currencySymbol });
  }

  public formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  public formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }
}